import { afField } from '@autodromo/contextual-fields'
import React, { forwardRef, useMemo, useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { EmblaCarouselType } from 'embla-carousel'
import { NextButton, PrevButton, ProgressBar, usePrevNextButtons } from '../../components/EmblaCarouselControls/EmblaCarouselControls'
import { sanitizeSvg } from '../../utils/sanitize'
import { interpolate, InterpolateResult, type DynamicStructure } from '../../utils/interpolate'
import { ActivePalette } from '../../utils/activePalette/types'
import { activeHidden, BlockVisibility } from '../common/BlockVisibility';
import { DynamicHeadingConfig } from '../../components/DynamicHeading/DynamicHeading'
import { DynamicCard, DynamicCardProps } from '../../components/DynamicCard/DynamicCard'
import { CountdownBadge } from '../../components/CountdownBadge'
import type { CountdownConfig, CountdownColors } from '../../components/Countdown'

import { BackgroundVideo } from '../../components/BackgroundVideo/BackgroundVideo'
import {
  DynamicSection,
  DynamicContainer,
  DynamicHeader,
  DynamicTitle,
  DynamicDescription,
  CarouselWrapper,
  CarouselContainer,
  CarouselViewport,
  CarouselContainerInner,
  CarouselSlide,
  CarouselSlideCard,
  CarouselPrev,
  CarouselNext,
  CarouselProgress,
  CarouselModeWrapper,
  GridWrapper,
  GridItem,
  MobileCarouselWrapper,
  MobileCarouselItem,
  BackgroundImage,
  BackgroundColor,
  BackgroundShadow,
  Buttons,
  ButtonItem
} from './DynamicCarouselAndGrid.styles'
import { EmptyState } from '../../components/EmptyState'

export interface DynamicCarouselAndGridProps<T> extends React.HTMLAttributes<HTMLDivElement>, ActivePalette, BlockVisibility {
  dynamicStructure?: DynamicStructure
  htmlId?: string
  blockTitle?: string
  blockTitleColor?: string
  blockDescription?: string
  blockDescriptionColor?: string

  buttons: ButtonProps[],

  // Background props
  backgroundType?: 'image' | 'color' | 'video'
  backgroundImage?: {
    desktop: {
      render: (props: T) => JSX.Element
      props: T
    }
    mobile: {
      render: (props: T) => JSX.Element
      props: T
    }
  }
  typeVideo?: 'url' | 'embed'
  urlVideo?: string
  embedVideo?: string
  loopVideo?: boolean
  audioVideo?: boolean
  shadowColor?: string

  cardConfig: {
    image?: {
      render: (props: T) => JSX.Element
      props: T
      template?: string
    }
    tag?: string
    title: string
    description?: string
    subtitle?: string
    buttonText?: string
    buttonLink?: string
    openLinkInNewTab?: boolean
  }

  visibility?: {
    image?: boolean
    tag?: boolean
    title?: boolean
    description?: boolean
    subtitle?: boolean
    button?: boolean
  }

  displayMode: 'carousel' | 'grid'
  cardType: 'padrao' | 'simples' | 'sobreposto' | 'seminovos'

  carouselConfig: {
    maxItems?: number
    itemsPerSlide?: 1 | 2 | 3 | 4 | 5 | 6
    itemGap?: number
    navigationType: 'none' | 'arrowsAndBar' | 'arrows' | 'bar'
    navigationColor?: string
    infinite?: boolean
    autoplay?: boolean
    autoplaySpeed?: 2 | 4 | 6 | 8 | 10
  }

  gridConfig: {
    maxItems?: number
    itemsPerRow?: number
    gap?: number
    showAsCarouselOnMobile?: boolean
  }

  cardStyleConfig: {
    contentAlignment: 'left' | 'center'
    buttonStyle: 'button' | 'link'
    imageAspectRatio?: 'fixed' | 'auto'
  }

  blockLayoutConfig: {
    textAlignment: 'left' | 'center'
    padding?: {
      typeMeasure?: string
      desktop?: { paddingTop?: number; paddingBottom?: number }
      mobile?: { paddingTop?: number; paddingBottom?: number }
    }
  }

  designConfig?: {
    backgroundColor?: string
    cardBackgroundColor?: string
    cardBorderColor?: string
    cardTitleColor?: string
    cardDescriptionColor?: string
    cardBodyColor?: string
    cardBorderRadius?: string
    specificationIconColor?: string
    // Card button colors
    cardButtonBackgroundColor?: string
    cardButtonBorderColor?: string
    cardButtonTextColor?: string
    cardButtonLinkColor?: string
    // General button colors
    buttonBackgroundColor?: string
    buttonTextColor?: string
    buttonBorderColor?: string
    buttonBorderRadius?: string
    linkColor?: string
    tagBackgroundColor?: string
    tagBorderColor?: string
    tagTextColor?: string
  }

  // Cronômetro de urgência (lê a data de fim de exibição — unpublishAt — de cada item).
  // Contrato compartilhado (components/Countdown); posição é do badge (esquerda/direita).
  countdownConfig?: Omit<CountdownConfig, 'position'> & { position?: 'left' | 'right' }
  countdownColors?: CountdownColors

  titleTag?: DynamicHeadingConfig['titleTag']
  descriptionTag?: DynamicHeadingConfig['titleTag'] | 'p'
  blockTitleTag?: DynamicHeadingConfig['titleTag'] | 'p'
  blockDescriptionTag?: DynamicHeadingConfig['titleTag'] | 'p'
  preload?: boolean

  // Configuração de especificações para layout seminovos
  specificationsConfig?: {
    items: Array<{
      id: number
      icon: string
      title: string
      contentKey: string
      isActive: boolean
    }>
  }
}

export interface ProcessedSpecification {
  icon: string
  title: React.ReactNode
  content?: InterpolateResult
}

interface ProcessedItem {
  id: number
  image?: React.ReactNode
  tag?: InterpolateResult
  title: InterpolateResult
  description?: InterpolateResult
  subtitle?: InterpolateResult
  buttonText?: InterpolateResult
  buttonLink?: string
  specifications?: ProcessedSpecification[]
  endDate?: string
}

type MenuRedirectType = 'page' | 'external_url' | 'scroll_to_block';

interface MenuRedirectConfig {
  type: MenuRedirectType;
  pageSlug?: string;
  url?: string;
  openInNewTab?: boolean;
  scrollPageSlug?: string;
  blockId?: string;
}

interface ButtonProps {
  id?: number;
  isActive: boolean;
  text?: string;
  textColor: string;
  backgroundColor: string;
  borderColor: string;
  icon: string;
  showIcon?: boolean;
  buttonIconAlignment?: 'left' | 'right';
  redirect?: MenuRedirectConfig;
  // Legacy fields for backward compatibility
  link?: string;
  openInNewTab?: boolean;
}


const DynamicCarouselAndGridInner = <T extends object>(
  {
    dynamicStructure,
    htmlId,
    blockTitle,
    blockTitleColor,
    blockDescription,
    blockDescriptionColor,
    backgroundType = 'color',
    backgroundImage,
    typeVideo,
    urlVideo,
    embedVideo,
    loopVideo,
    audioVideo,
    shadowColor,
    cardConfig,
    visibility = {
      image: true,
      tag: true,
      title: true,
      description: true,
      button: true
    },
    displayMode,
    buttons,
    cardType,
    carouselConfig,
    gridConfig,
    cardStyleConfig,
    blockLayoutConfig,
    designConfig,
    titleTag = 'h2',
    descriptionTag = 'p',
    blockTitleTag,
    blockDescriptionTag,
    activePalette,
    hideOnMobile,
    hideOnDesktop,
    specificationsConfig,
    countdownConfig,
    countdownColors
  }: DynamicCarouselAndGridProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const [scrollProgress, setScrollProgress] = useState(0)

  const CARD_TYPE_MAP: Record<string, DynamicCardProps['type']> = {
    padrao: 'default',
    simples: 'simple',
    sobreposto: 'overlaid',
    seminovos: 'seminovos'
  } as const

  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 1,
    loop: carouselConfig.infinite ?? false,
    align: 'start'
  })

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextButtons(emblaApi)

  const onScroll = useCallback((api: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, api.scrollProgress()))
    setScrollProgress(progress * 100)
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi
      .on('reInit', onScroll)
      .on('scroll', onScroll)
      .on('slideFocus', onScroll)
  }, [emblaApi, onScroll])

  useEffect(() => {
    if (!emblaApi || !carouselConfig.autoplay) return

    const autoplay = () => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext()
      } else if (carouselConfig.infinite) {
        emblaApi.scrollTo(0)
      }
    }

    const autoplayInterval = setInterval(autoplay, Number(carouselConfig.autoplaySpeed || 4) * 1000)

    return () => clearInterval(autoplayInterval)
  }, [emblaApi, carouselConfig.autoplay, carouselConfig.autoplaySpeed, carouselConfig.infinite])

  const dynamicContent = (Array.isArray(dynamicStructure?.section)
    ? dynamicStructure.section
    : []) as Array<Record<string, any>>
    
  const processedItems = useMemo<ProcessedItem[]>(() => {
    if (!dynamicContent || dynamicContent.length === 0) return []

    return dynamicContent.map((item, index) => {
      const itemStructure: DynamicStructure = {
        ...dynamicStructure,
        section: item
      }

      // Fallback de alt para a imagem dinâmica: o título do card (nome do item),
      // resolvido do CMS. Sem isso, entry sem alt próprio publica <img> sem texto
      // alternativo — preencher à mão em N registros é inviável (ver DynamicList).
      const cardTitleText = cardConfig.title
        ? interpolate({ structure: itemStructure, template: cardConfig.title }).text?.trim() || ''
        : '';

      const processedImage = cardConfig.image
        ? (() => {
          let imageProps = { ...cardConfig.image.props } as Record<string, any>;

          // Interpola src se for template
          if (typeof imageProps.src === 'string' && imageProps.src.includes('{{')) {
            const image = interpolate({ structure: itemStructure, template: imageProps.src }).image;
            if (image) {
              imageProps = {
                ...imageProps,
                src: image.src || '',
                alt: image.alt || imageProps.alt || cardTitleText || ''
              };
            }
          }

          // Não renderiza imagem se não tiver src válido
          if (!imageProps.src || imageProps.src.includes('{{')) {
            return undefined;
          }

          return cardConfig.image.render({
            ...imageProps,
            className: 'image'
          } as T);
        })()
        : undefined

      // Monta o buttonLink com o slug do item
      const baseLink = cardConfig.buttonLink ? interpolate({ structure: itemStructure, template: cardConfig.buttonLink }).text : undefined;
      const itemSlug = item.slug || item.Slug || '';
      // Normaliza o baseLink para começar com / (link absoluto)
      const normalizedBaseLink = baseLink && !baseLink.startsWith('/') ? `/${baseLink}` : baseLink;

      // Prepend site slug if channel has one configured
      const siteSlug = dynamicStructure?.channel?.slug;
      const basePath = siteSlug ? `/${siteSlug}` : '';

      const buttonLink = normalizedBaseLink && itemSlug
        ? `${basePath}${normalizedBaseLink}/${itemSlug}`
        : normalizedBaseLink
          ? `${basePath}${normalizedBaseLink}`
          : normalizedBaseLink;

      // Process specifications for seminovos card type
      const processedSpecifications: ProcessedSpecification[] | undefined =
        cardType === 'seminovos' && specificationsConfig?.items
          ? specificationsConfig.items
            .filter(spec => spec.isActive)
            .map(spec => ({
              icon: spec.icon,
              title: spec.title,
              content: spec.contentKey ? interpolate({ structure: itemStructure, template: spec.contentKey }) : undefined
            }))
          : undefined

      return {
        id: item.id || item.slug || item.Slug || index,
        image: processedImage,
        tag: cardConfig.tag ? interpolate({ structure: itemStructure, template: cardConfig.tag }) : undefined,
        title: interpolate({ structure: itemStructure, template: cardConfig.title || '' }),
        description: cardConfig.description ? interpolate({ structure: itemStructure, template: cardConfig.description }, { parseHtml: true }) : undefined,
        subtitle: cardConfig.subtitle ? interpolate({ structure: itemStructure, template: cardConfig.subtitle }) : undefined,
        buttonText: interpolate({ structure: itemStructure, template: cardConfig.buttonText || 'Saiba mais' }),
        buttonLink,
        specifications: processedSpecifications,
        // Data de fim é um ATRIBUTO nativo do registro (unpublishAt), não um campo mapeável.
        // Data de fim de exibição (unpublishAt) do próprio item — o badge decide se exibe.
        endDate: item.unpublishAt as string | undefined
      }
    })
  }, [dynamicContent, dynamicStructure, cardConfig, cardType, specificationsConfig])

  const displayItems = useMemo(() => {
    const max = displayMode === 'carousel'
      ? carouselConfig.maxItems
      : gridConfig.maxItems
    return max ? processedItems.slice(0, max) : processedItems
  }, [processedItems, displayMode, carouselConfig.maxItems, gridConfig.maxItems])

  const shouldShowEmptyState = !dynamicContent || dynamicContent.length === 0

  const renderNavigation = () => {
    const { navigationType, navigationColor } = carouselConfig

    if (navigationType === 'none') return null

    return (
      <>
        {(navigationType === 'arrows' || navigationType === 'arrowsAndBar') && (
          <>
            <CarouselPrev>
              <PrevButton
                elementColor={navigationColor}
                onClick={onPrevButtonClick}
                disabled={prevBtnDisabled}
                data-testid="prev-button"
              />
            </CarouselPrev>
            <CarouselNext>
              <NextButton
                elementColor={navigationColor}
                onClick={onNextButtonClick}
                disabled={nextBtnDisabled}
                data-testid="next-button"
              />
            </CarouselNext>
          </>
        )}
        {(navigationType === 'bar' || navigationType === 'arrowsAndBar') && (
          <CarouselProgress>
            <ProgressBar elementColor={navigationColor} scrollProgress={scrollProgress} data-testid="progress-bar" />
          </CarouselProgress>
        )}
      </>
    )
  }

  const renderButtons = () => {
    const activeButtons = buttons?.filter((button) => button.isActive) || []
    if (activeButtons.length === 0) return null

    return (
      <Buttons>
        {activeButtons.map((button) => (
          <ButtonItem
            key={button.id || `${button.text}-${button.redirect?.url || button.link || ''}`}
            data-af-field={afField('buttons', button.id, 'text')}
            href={button.redirect?.url || button.link}
            target={button.redirect?.openInNewTab || button.openInNewTab ? "_blank" : "_self"}
            buttonBgColor={button.backgroundColor || designConfig?.buttonBackgroundColor}
            buttonTextColor={button.textColor || designConfig?.buttonTextColor}
            buttonBorderColor={button.borderColor || designConfig?.buttonBorderColor}
          >
            {button.showIcon && button.buttonIconAlignment === 'left' && (
              <span
                style={{
                  '--icon-color': button.textColor || designConfig?.buttonTextColor || 'var(--buttons-text)',
                } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(button.icon) }}
              ></span>
            )}
            {button.text}
            {button.showIcon && button.buttonIconAlignment === 'right' && (
              <span
                style={{
                  '--icon-color': button.textColor || designConfig?.buttonTextColor || 'var(--buttons-text)',
                } as React.CSSProperties}
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(button.icon) }}
              ></span>
            )}
          </ButtonItem>
        ))}
      </Buttons>
    )
  }

  const renderCard = (item: ProcessedItem) => (
    <DynamicCard
      fieldPath="cardConfig"
      key={item.id}
      type={CARD_TYPE_MAP[cardType]}
      image={item.image}
      countdown={countdownConfig ? (
        <CountdownBadge config={countdownConfig as CountdownConfig} colors={countdownColors} unpublishAt={item.endDate} />
      ) : undefined}
      countdownPosition={countdownConfig?.position ?? 'left'}
      tag={item.tag}
      title={item.title}
      description={item.description}
      subtitle={item.subtitle}
      buttonText={item.buttonText}
      buttonLink={item.buttonLink}
      openLinkInNewTab={cardConfig.openLinkInNewTab}
      alignment={cardStyleConfig.contentAlignment}
      buttonStyle={cardStyleConfig.buttonStyle}
      imageAspectRatio={cardStyleConfig.imageAspectRatio}
      showImage={visibility.image}
      showTag={visibility.tag}
      showTitle={visibility.title}
      showDescription={visibility.description}
      titleColor={designConfig?.cardTitleColor}
      descriptionColor={designConfig?.cardDescriptionColor || designConfig?.cardBodyColor}
      showSubtitle={visibility.subtitle}
      showButton={visibility.button}
      backgroundColor={designConfig?.cardBackgroundColor}
      borderColor={designConfig?.cardBorderColor}
      borderRadius={designConfig?.cardBorderRadius}
      tagBgColor={designConfig?.tagBackgroundColor}
      tagBorderColor={designConfig?.tagBorderColor}
      tagTextColor={designConfig?.tagTextColor}
      buttonBgColor={designConfig?.cardButtonBackgroundColor || designConfig?.buttonBackgroundColor}
      buttonTextColor={designConfig?.cardButtonTextColor || designConfig?.buttonTextColor}
      buttonBorderColor={designConfig?.cardButtonBorderColor || designConfig?.buttonBorderColor}
      buttonBorderRadius={designConfig?.buttonBorderRadius}
      buttonLinkColor={designConfig?.cardButtonLinkColor || designConfig?.linkColor}
      htmlId={htmlId}
      specsFieldPath="specificationsConfig/items"
      specifications={item.specifications}
      specificationIconColor={designConfig?.specificationIconColor}
    />
  )

  const renderCarousel = () => {
    const itemGap = carouselConfig.itemGap || 24
    const itemsPerSlide = carouselConfig.itemsPerSlide || 3

    return (
      <CarouselModeWrapper>
        <CarouselWrapper>
          <CarouselContainer className="embla">
            <CarouselViewport className="embla__viewport" ref={emblaRef}>
              <CarouselContainerInner
                className="embla__container"
                itemGap={itemGap}
              >
                {displayItems.map((item) => (
                  <CarouselSlide
                    key={`carousel-slide-${item.id}`}
                    className="embla__slide"
                    itemGap={itemGap}
                    itemsPerSlide={itemsPerSlide}
                    data-testid="carousel-slide"
                  >
                    <CarouselSlideCard itemsPerSlide={itemsPerSlide} itemGap={itemGap}>
                      {renderCard(item)}
                    </CarouselSlideCard>
                  </CarouselSlide>
                ))}
              </CarouselContainerInner>
            </CarouselViewport>
          </CarouselContainer>
          {renderNavigation()}
        </CarouselWrapper>
      </CarouselModeWrapper>
    )
  }

  const renderGrid = () => {
    const showAsCarouselOnMobile = gridConfig.showAsCarouselOnMobile ?? true
    const itemsPerRow = gridConfig.itemsPerRow || 3
    const gap = gridConfig.gap || 24

    return (
      <>
        <GridWrapper
          itemsPerRow={itemsPerRow}
          gap={gap}
          showAsCarouselOnMobile={showAsCarouselOnMobile}
          data-testid="grid-wrapper"
        >
          {displayItems.map((item) => (
            <GridItem
              key={`grid-item-${item.id}`}
              itemsPerRow={itemsPerRow}
              gap={gap}
              data-testid="grid-item"
            >
              {renderCard(item)}
            </GridItem>
          ))}
        </GridWrapper>

        {showAsCarouselOnMobile && (
          <MobileCarouselWrapper data-testid="mobile-carousel">
            {displayItems.map((item) => (
              <MobileCarouselItem key={`mobile-carousel-${item.id}`}>
                {renderCard(item)}
              </MobileCarouselItem>
            ))}
          </MobileCarouselWrapper>
        )}
      </>
    )
  }

  const Video = () => {
    return (
      <>
        {typeVideo === 'url' && (
          <BackgroundVideo
            url={urlVideo}
            loop={loopVideo}
            audio={audioVideo}
            data-testid="dynamic-carousel-video-url"
          />
        )}

        {typeVideo === 'embed' && (
          <BackgroundVideo
            embed={embedVideo}
            loop={loopVideo}
            audio={audioVideo}
            data-testid="dynamic-carousel-video-embed"
          />
        )}
      </>
    )
  }

  return (
    <DynamicSection
      ref={ref}
      id={htmlId}
      padding={blockLayoutConfig.padding}
      className={`${activePalette || ''} ${activeHidden({ hideOnMobile, hideOnDesktop })}`}
      data-testid="dynamic-carousel-and-grid"
    >
      {backgroundImage && backgroundType === 'image' && (
        <BackgroundImage>
          {backgroundImage?.desktop?.render({ ...backgroundImage.desktop.props, className: 'desktop-image' })}
          {backgroundImage?.mobile?.render({ ...backgroundImage.mobile.props, className: 'mobile-image' })}
        </BackgroundImage>
      )}

      {backgroundType === 'video' && <Video />}

      {(backgroundType === 'video' || backgroundType === 'image') && (
        <BackgroundShadow shadowColor={shadowColor} />
      )}

      {backgroundType === 'color' && (
        <BackgroundColor backgroundcolor={designConfig?.backgroundColor} />
      )}

      <DynamicContainer data-anim-stagger>
        {(blockTitle || blockDescription) && (
          <DynamicHeader textAlignment={blockLayoutConfig.textAlignment}>
            {blockTitle && (
              <DynamicTitle
                data-af-field="blockTitle"
                titleTag={(blockTitleTag ?? titleTag) as DynamicHeadingConfig['titleTag']}
                color={blockTitleColor}
              >
                {blockTitle}
              </DynamicTitle>
            )}
            {blockDescription && (
              <DynamicDescription data-af-field="blockDescription" titleTag={blockDescriptionTag ?? descriptionTag} color={blockDescriptionColor}>
                {blockDescription}
              </DynamicDescription>
            )}
          </DynamicHeader>
        )}

        {shouldShowEmptyState ? (
          <EmptyState title='Nenhum item disponível' description='Não há itens cadastrados para exibição no momento.'/>
        ) : (
          <>
            {displayMode === 'carousel' ? renderCarousel() : renderGrid()}
            {renderButtons()}
          </>
        )}
      </DynamicContainer>
    </DynamicSection>
  )
}

export const DynamicCarouselAndGrid = forwardRef(DynamicCarouselAndGridInner)
